// ts-rest contract for the Entity AML Assessment (SEP-186 + SEP-228).
//
// An `EAML-####` record per assessment occasion, stacked on an ENTITY
// Suitability Record over time — the structural sibling of the Contact-level
// AML Assessment contract, anchored to the SUR instead of a Contact. The SUR
// Details page needs two things:
//   - the summary its "Entity AML" section shows (band + score of the most
//     recently COMPLETED assessment),
//   - the stacked history rows (Reference | Score | Risk | Completed By |
//     Completed Date | Status).
//
// The field table itself is imported directly from the package
// (`ENTITY_AML_FIELDS`, `AML_COMPANY_QUESTIONS`, `COUNTRIES`); it is not an
// endpoint.
//
// TWO INSTRUMENTS SHARE THE DTO, and `kind` tells them apart. A `kind` null
// record is a SEP-186 one: its stored `score` travels, and its band derives
// from that score. A `kind: 'company'` record is a SEP-228 one: it answers the
// company question set, and its figures travel on `calculatedScore`,
// `calculatedBand` and the `questionnaire` block — the same shapes the Contact
// AML contract uses. The SEP-186 figures are null on such a record, so a
// surface can never band it from the wrong instrument.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { staffActionErrorSchema } from './agent';
import {
    amlDecisionSchema,
    amlEddStatusSchema,
    amlQuestionnaireDerivedSchema,
} from './aml-assessment';

const c = initContract();

import { errorBody } from '../schemas/common';

// SEP-228 adds the two states between the author and Compliance (§23, §29).
const statusSchema = z.enum(['draft', 'awaiting_compliance', 'completed', 'rejected']);
const riskBandSchema = z.enum(['low', 'medium', 'high']);
const bandV2Schema = z.enum(['low', 'moderate', 'high']);

/**
 * The stored field values, keyed by field key. Yes/No questions store `'yes'` /
 * `'no'`; the country field an ISO 3166-1 alpha-2 code; dates `YYYY-MM-DD`; the
 * rest free text. Only non-blank fields are present.
 */
export const entityAmlAnswersSchema = z.record(z.string(), z.string());
export type EntityAmlAnswersDto = z.infer<typeof entityAmlAnswersSchema>;

export const entityAmlAssessmentDtoSchema = z.object({
    id: z.string(),
    /** EAML-####. */
    reference: z.string().nullable(),
    suitabilityRecordId: z.string(),
    /** The parent record's SUR-#### — the history grid's context column. */
    suitabilityRecordReference: z.string().nullable(),
    /** The owning IM (a `users` row id). */
    imUserId: z.string(),
    status: statusSchema,
    answers: entityAmlAnswersSchema,
    /** The conditional cash-generative-business selection; null unless the
     * business-source-of-income question is answered `yes`. SEP-186 only. */
    highRiskBusiness: z.string().nullable(),
    /** THE STORED SEP-186 SCORE. Null on a SEP-228 record. */
    score: z.number().nullable(),
    /** Derived from `score`. Null on a SEP-228 record. */
    band: riskBandSchema.nullable(),
    /** "Low" / "Medium" / "High". Null on a SEP-228 record. */
    bandLabel: z.string().nullable(),
    /** Answered / answerable, for the ACTIVE instrument of the record. */
    answered: z.number(),
    answerable: z.number(),
    /** Calendar completion date (`YYYY-MM-DD`) — null while draft. */
    completionDate: z.string().nullable(),
    createdAt: z.string(),
    /** Completion timestamp — null while draft. */
    completedAt: z.string().nullable(),
    /** The "Completed By" staff user id — null while draft. */
    completedByUserId: z.string().nullable(),
    /** Display name for `completedByUserId`, resolved server-side. */
    completedByName: z.string().nullable(),
    // --- The SEP-228 workflow. Every field is null on a SEP-186 record. -----
    /** Which instrument the record answers. Null marks a SEP-186 record. */
    kind: z.enum(['company']).nullable(),
    /** The SEP-228 derived block. Null on a SEP-186 record. */
    questionnaire: amlQuestionnaireDerivedSchema.nullable(),
    submittedAt: z.string().nullable(),
    submittedByUserId: z.string().nullable(),
    submittedByName: z.string().nullable(),
    /** The figure the instrument gave at submission, kept through an override. */
    calculatedScore: z.number().nullable(),
    calculatedBand: bandV2Schema.nullable(),
    /** The risk that stands: the override where there is one, else the calculated band. */
    effectiveBand: bandV2Schema.nullable(),
    eddRequired: z.boolean().nullable(),
    eddReasons: z.array(z.string()).nullable(),
    eddStatus: amlEddStatusSchema.nullable(),
    eddNotes: z.string().nullable(),
    eddCompletedAt: z.string().nullable(),
    eddCompletedByUserId: z.string().nullable(),
    eddCompletedByName: z.string().nullable(),
    decision: amlDecisionSchema.nullable(),
    complianceReviewerUserId: z.string().nullable(),
    complianceReviewerName: z.string().nullable(),
    complianceDecisionAt: z.string().nullable(),
    complianceNotes: z.string().nullable(),
    /** Set only by an override. Null means the calculated band stands (§30). */
    finalBand: bandV2Schema.nullable(),
    overrideReason: z.string().nullable(),
    overriddenByUserId: z.string().nullable(),
    overriddenByName: z.string().nullable(),
    overriddenAt: z.string().nullable(),
    /** The day the next review falls due (§32). */
    nextReviewDate: z.string().nullable(),
});
export type EntityAmlAssessmentDto = z.infer<typeof entityAmlAssessmentDtoSchema>;

/**
 * What the record's Entity AML section header shows: the risk band of the most
 * recently COMPLETED assessment. Every field is null when there is none.
 */
export const entityAmlSummarySchema = z.object({
    assessmentId: z.string().nullable(),
    reference: z.string().nullable(),
    band: riskBandSchema.nullable(),
    /** "Low" / "Medium" / "High". */
    bandLabel: z.string().nullable(),
    score: z.number().nullable(),
    completionDate: z.string().nullable(),
});
export type EntityAmlSummary = z.infer<typeof entityAmlSummarySchema>;

/** The summary + every assessment held against the record, newest first. */
export const listEntityAmlAssessmentsResponseSchema = z.object({
    suitabilityRecordId: z.string(),
    suitabilityRecordReference: z.string(),
    /** The entity the record is held by, for the grid's context. */
    entityName: z.string().nullable(),
    summary: entityAmlSummarySchema,
    assessments: z.array(entityAmlAssessmentDtoSchema),
    /**
     * The answers that the newest entity application of the client suggests
     * (SEP-293), by AML key, or null when the client has none. The form shows
     * each one with its source, and stores nothing until the IM saves.
     */
    applicationSuggestions: z
        .object({
            formTitle: z.string(),
            submittedAt: z.string(),
            answers: z.record(z.string(), z.string()),
        })
        .nullable(),
});
export type ListEntityAmlAssessmentsResponse = z.infer<
    typeof listEntityAmlAssessmentsResponseSchema
>;

/**
 * Save body — the form's editable fields. Nothing derived is sent: the score is
 * computed at the seam from `scoreEntityAml`, so a body cannot dictate it.
 */
const saveBodySchema = z.object({
    /** Absent → create a new draft; present → patch that draft. */
    id: z.string().optional(),
    answers: entityAmlAnswersSchema,
    highRiskBusiness: z.string().nullable().optional(),
});

/** Complete body — the "Completed By" decision (defaults to the caller). */
const completeBodySchema = z
    .object({
        completedByUserId: z.string().optional(),
    })
    .optional();

// --- The SEP-228 workflow bodies (§21, §26, §28, §29, §30) -----------------

/** Submit body — nothing to decide. The author presses one button (§21). */
const submitBodySchema = z.object({}).optional();

/** The EDD section Compliance completes (§26). */
const eddBodySchema = z.object({
    status: amlEddStatusSchema,
    /** Required to complete an EDD; a completion with no note tells nobody why. */
    notes: z.string().nullable().optional(),
});

/** Approve or reject a Moderate or High record (§28, §29). */
const decisionBodySchema = z.object({
    decision: z.enum(['approved', 'rejected']),
    /** The reviewer's own note. Required on a rejection. */
    notes: z.string().nullable().optional(),
});

/** Override the calculated risk (§30). The reason is never optional. */
const overrideBodySchema = z.object({
    finalBand: bandV2Schema,
    reason: z.string().min(1),
});

export const entityAmlAssessmentContract = c.router(
    {
        listEntityAmlAssessments: {
            method: 'GET',
            path: '/suitability-records/:recordId/entity-aml-assessments',
            pathParams: z.object({ recordId: z.string() }),
            responses: {
                200: listEntityAmlAssessmentsResponseSchema,
                403: errorBody,
                404: errorBody,
            },
            summary: "An entity Suitability Record's Entity AML summary + history (newest first)",
        },
        saveEntityAmlAssessment: {
            method: 'POST',
            path: '/suitability-records/:recordId/entity-aml-assessments/save',
            pathParams: z.object({ recordId: z.string() }),
            body: saveBodySchema,
            responses: {
                200: z.object({ assessment: entityAmlAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: "Create or patch an entity record's draft Entity AML assessment",
        },
        completeEntityAmlAssessment: {
            method: 'POST',
            path: '/suitability-records/entity-aml-assessments/:id/complete',
            pathParams: z.object({ id: z.string() }),
            body: completeBodySchema,
            responses: {
                200: z.object({ assessment: entityAmlAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Complete an Entity AML assessment (draft → completed)',
        },
        // --- The SEP-228 workflow doors --------------------------------------
        submitEntityAmlAssessment: {
            method: 'POST',
            path: '/suitability-records/entity-aml-assessments/:id/submit',
            pathParams: z.object({ id: z.string() }),
            body: submitBodySchema,
            responses: {
                200: z.object({ assessment: entityAmlAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
                409: staffActionErrorSchema,
            },
            summary: 'Submit the answers for scoring, and route the result (§21)',
        },
        setEntityAmlEdd: {
            method: 'POST',
            path: '/suitability-records/entity-aml-assessments/:id/edd',
            pathParams: z.object({ id: z.string() }),
            body: eddBodySchema,
            responses: {
                200: z.object({ assessment: entityAmlAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Record the enhanced due diligence section (§26)',
        },
        decideEntityAmlAssessment: {
            method: 'POST',
            path: '/suitability-records/entity-aml-assessments/:id/decide',
            pathParams: z.object({ id: z.string() }),
            body: decisionBodySchema,
            responses: {
                200: z.object({ assessment: entityAmlAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
                // An open EDD blocks an approval (§26).
                409: staffActionErrorSchema,
            },
            summary: 'Approve or reject a Moderate or High assessment (§28, §29)',
        },
        overrideEntityAmlRisk: {
            method: 'POST',
            path: '/suitability-records/entity-aml-assessments/:id/override',
            pathParams: z.object({ id: z.string() }),
            body: overrideBodySchema,
            responses: {
                200: z.object({ assessment: entityAmlAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Override the calculated risk, with a mandatory reason (§30)',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
